import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { DwComponent, DwComponentType, DwExpressionService, DwMetaDataFormApi, DwMetaDataFormSectionStateService, DwMetaDataFormStateService, DwMetaDataService, DwMetaDataServiceToken, DwSectionReadonlyBaseComponent, DwUiConfigRegistryService, DwUIMetaDataConfig, DwUIMetaDataConfigToken } from '@devwareapps/devware-cap';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AppMetaData, AppMetaDataItemNames, CourseDocumentItemEntity, DocumentAiProcessingStatusAllItems, DocumentItemEntity, ReferenceStatusAllItems } from '../../../../meta-data/app-meta-data.service';
import { DocumentsRepositoryService } from '../../services/documents-repository.service';

interface CourseOption {
  label: string;
  value: number;
  detail?: string;
}

@DwComponent({
  key: 'document-course-link-form-section',
  name: 'Document Course Link Form Section',
  componentType: DwComponentType.formSection,
  parentItemName: AppMetaDataItemNames.DocumentItem,
  isGlobal: false,
})
@Component({
  selector: 'app-document-course-link-form-section',
  template: `
    <div class="dw-form-field">
      <label class="label">Courses</label>
      <p-multiSelect
        *ngIf="!isSectionReadOnly(); else readonly"
        [options]="courseOptions"
        [(ngModel)]="selectedCourseIds"
        [ngModelOptions]="{ standalone: true }"
        optionLabel="label"
        optionValue="value"
        defaultLabel="Select courses"
        display="chip"
        [filter]="true"
        [showToggleAll]="false"
        [disabled]="!associativeList"
        [style]="{'width':'100%'}"
        appendTo="body"
        baseZIndex="2000"
        autoZIndex="false"
        [ngClass]="{ 'multi-edit-auto-expand': true }"
        (ngModelChange)="updateSelectedCourses()">
      </p-multiSelect>
      <small *ngIf="!isSectionReadOnly()" class="dw-text-muted">
        Removing a course marks the document link inactive so it can be restored later.
      </small>
    </div>
    <ng-template #readonly>
      <div [hidden]="!displayItems.length" class="dw-chip-container">
        <ng-container *ngFor="let item of displayItems; let i = index">
          <div class="dw-chip">
            <span class="dw-chip-label">{{ item }}</span>
          </div>
        </ng-container>
      </div>
      <div [hidden]="displayItems.length > 0">
        None
      </div>
    </ng-template>
  `
})
export class DocumentCourseLinkFormSectionComponent extends DwSectionReadonlyBaseComponent implements OnInit, OnDestroy {
  associativeList?: AbstractControl;
  formApi: DwMetaDataFormApi;
  selectedCourseIds: number[] = [];
  courseOptions: CourseOption[] = [];
  displayItems: string[] = [];

  private readonly controlName = 'CourseDocumentItem';
  private readonly destroy$ = new Subject<void>();

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwFormSectionStateService: DwMetaDataFormSectionStateService,
    protected dwFormStateService: DwMetaDataFormStateService,
    private documentsRepositoryService: DocumentsRepositoryService
  ) {
    super(dwExpressionService, dwFormSectionStateService, dwFormStateService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.associativeList = this.formGroup.get(this.controlName);

    if (!this.associativeList) {
      console.warn(`Form control ${this.controlName} not found in form group.`);
      return;
    }

    this.runAfterFormDataLoaded(() => {
      this.loadCourseOptions();
      this.syncSelectedCourses();
    });

    this.formApi.getFormDataChanges()
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.syncSelectedCourses());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  sectionReadOnlyChanged(readOnly: boolean): void {
    return;
  }

  updateSelectedCourses(): void {
    const documentItem = this.formApi.getFormData<DocumentItemEntity>();
    const allLinks = [...((this.associativeList?.value || []) as CourseDocumentItemEntity[])];
    let addedOrReactivatedLink = false;

    allLinks
      .filter(link => link.CourseDocumentItemStatusId !== ReferenceStatusAllItems.Deleted)
      .forEach(link => {
        link._isDeleted = false;
        link.CourseDocumentItemStatusId = ReferenceStatusAllItems.Inactive;
      });

    for (const courseId of this.selectedCourseIds) {
      let existingLink = allLinks.find(link =>
        link.CourseId === courseId
        && link.CourseDocumentItemStatusId !== ReferenceStatusAllItems.Deleted);

      if (!existingLink) {
        existingLink = AppMetaData.CourseDocumentItem.CreateEntity();
        existingLink.DocumentItemId = documentItem?.DocumentItemId;
        existingLink.CourseId = courseId;
        allLinks.push(existingLink);
        addedOrReactivatedLink = true;
      } else if (existingLink.CourseDocumentItemStatusId !== ReferenceStatusAllItems.Active) {
        addedOrReactivatedLink = true;
      }

      existingLink._isDeleted = false;
      existingLink.CourseDocumentItemStatusId = ReferenceStatusAllItems.Active;
    }

    this.associativeList?.setValue(allLinks);

    if (addedOrReactivatedLink) {
      this.queueDocumentForCourseLinkReprocess(documentItem);
    }
  }

  private loadCourseOptions(): void {
    this.documentsRepositoryService.getActiveCourses()
      .pipe(takeUntil(this.destroy$))
      .subscribe(courses => {
        this.courseOptions = (courses || [])
          .filter(course => !!course.CourseId)
          .map(course => ({
            label: course.CourseName || `Course ${course.CourseId}`,
            value: course.CourseId!
          }));

        this.syncSelectedCourses();
      });
  }

  private syncSelectedCourses(): void {
    const activeCourseIds = (((this.associativeList?.value || []) as CourseDocumentItemEntity[])
      .filter(link => link.CourseDocumentItemStatusId === ReferenceStatusAllItems.Active && link._isDeleted !== true)
      .map(link => link.CourseId)
      .filter((courseId): courseId is number => !!courseId));

    this.selectedCourseIds = [...activeCourseIds];
    this.displayItems = this.selectedCourseIds
      .map(courseId => this.courseOptions.find(option => option.value === courseId)?.label || `Course ${courseId}`)
      .filter((label): label is string => !!label);
  }

  private queueDocumentForCourseLinkReprocess(documentItem?: DocumentItemEntity): void {
    if (!documentItem) {
      return;
    }

    if (documentItem.DocumentAiProcessingStatusId !== DocumentAiProcessingStatusAllItems.Complete
      && documentItem.DocumentAiProcessingStatusId !== DocumentAiProcessingStatusAllItems.ProcessingModuleDocumentLinks
      && documentItem.DocumentAiProcessingStatusId !== DocumentAiProcessingStatusAllItems.FailedProcessingModuleDocumentLinks) {
      return;
    }

    documentItem.DocumentAiProcessingStatusId = DocumentAiProcessingStatusAllItems.ReprocessRequested;
    documentItem.DocumentAiProcessingProgressText = 'Reprocess requested';
    documentItem.DocumentAiProcessingErrorText = null;
    documentItem.DocumentAiProcessingCompletedDateTime = null;
    documentItem.DocumentAiProcessingEstimatedCompletedDateTime = null;
    documentItem.DocumentAiProcessingLastUpdatedDateTime = new Date().toISOString();
  }
}
